import React from 'react';
import { Calendar } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { he } from 'date-fns/locale';
import { colors, radius } from '../designSystem';

interface Promotion {
  is_active?: boolean;
  discount_type?: 'percentage' | 'fixed';
  discount_value?: number;
  start_date?: string;
  end_date?: string;
}

interface Appointment {
  id: string;
  start_time: string;
  end_time: string;
  services: {
    id: string;
    name_he: string;
    price: number;
    image_url?: string;
    promotion?: Promotion;
  };
  users: { id: string; name: string };
  status: string;
}

interface AppointmentsSectionProps {
  isAuthenticated: boolean;
  appointments: Appointment[];
  loading: boolean;
  onBookAppointment: () => void;
  onAppointmentClick: (appointment: Appointment) => void;
  businessId: string;
}

export const AppointmentsSection: React.FC<AppointmentsSectionProps> = ({
  isAuthenticated,
  appointments,
  loading,
  onBookAppointment,
  onAppointmentClick,
  businessId,
}) => {
  return (
    <div className="px-6 py-6 border-b" style={{ borderColor: colors.divider }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>
          התורים שלי
        </h3>
        {isAuthenticated && (
          <button
            onClick={onBookAppointment}
            className="text-sm font-semibold"
            style={{ color: colors.accent }}
          >
            תור חדש
          </button>
        )}
      </div>

      {!isAuthenticated ? (
        <div className="text-center py-8">
          <p className="text-sm mb-2" style={{ color: colors.textSecondary }}>
            כדי לצפות בתורים שלך
          </p>
          <p className="text-sm font-semibold" style={{ color: colors.accent }}>
            יש להתחבר תחילה
          </p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-12">
          <div
            className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: colors.accent }}
          />
        </div>
      ) : appointments.length > 0 ? (
        <div className="space-y-3">
          {appointments.map((apt) => {
            const promotion = apt.services.promotion;
            const hasPromotion = promotion?.is_active && promotion?.discount_value;

            let displayPrice = apt.services.price;
            if (hasPromotion && promotion) {
              if (promotion.discount_type === 'percentage') {
                displayPrice = apt.services.price * (1 - (promotion.discount_value / 100));
              } else {
                displayPrice = apt.services.price - promotion.discount_value;
              }
            }
            const originalPrice = apt.services.price;

            const statusConfig = {
              confirmed: { bg: colors.successLight, text: colors.success, label: 'מאושר' },
              booked: { bg: colors.warningLight, text: colors.warning, label: 'ממתין לאישור' },
              canceled: { bg: colors.errorLight, text: colors.error, label: 'בוטל' },
              done: { bg: colors.secondary, text: colors.textSecondary, label: 'הושלם' }
            };
            const config = statusConfig[apt.status as keyof typeof statusConfig] || statusConfig.booked;

            return (
              <button
                key={apt.id}
                onClick={() => onAppointmentClick(apt)}
                className="w-full text-right p-4 border"
                style={{
                  borderRadius: radius.xl,
                  borderColor: colors.border,
                  backgroundColor: colors.white,
                }}
              >
                <div className="flex items-start gap-4">
                  {apt.services.image_url ? (
                    <img
                      src={apt.services.image_url}
                      alt={apt.services.name_he}
                      className="w-16 h-16 object-cover flex-shrink-0"
                      style={{ borderRadius: radius.lg }}
                    />
                  ) : (
                    <div
                      className="w-16 h-16 flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: colors.secondary,
                        borderRadius: radius.lg,
                      }}
                    >
                      <Calendar className="h-8 w-8" style={{ color: colors.textTertiary }} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="font-semibold text-lg" style={{ color: colors.textPrimary }}>
                        {apt.services.name_he}
                      </p>
                      <span
                        className="px-2 py-0.5 text-xs font-medium flex-shrink-0"
                        style={{
                          backgroundColor: config.bg,
                          color: config.text,
                          borderRadius: radius.full,
                        }}
                      >
                        {config.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm mb-2" style={{ color: colors.textSecondary }}>
                      <span>{format(parseISO(apt.start_time), 'd בMMMM, HH:mm', { locale: he })}</span>
                      <span>•</span>
                      <span>עם {apt.users.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {hasPromotion ? (
                        <>
                          <span className="text-sm line-through" style={{ color: colors.textTertiary }}>
                            ₪{originalPrice}
                          </span>
                          <span className="text-base font-bold" style={{ color: colors.success }}>
                            ₪{displayPrice}
                          </span>
                          <span
                            className="text-xs px-2 py-0.5"
                            style={{
                              backgroundColor: colors.successLight,
                              color: colors.success,
                              borderRadius: radius.full,
                            }}
                          >
                            מבצע
                          </span>
                        </>
                      ) : (
                        <span className="text-base font-bold" style={{ color: colors.textPrimary }}>
                          ₪{displayPrice}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: colors.secondary }}
          >
            <Calendar className="h-8 w-8" style={{ color: colors.textTertiary }} />
          </div>
          <p className="mb-4" style={{ color: colors.textSecondary }}>
            אין תורים קרובים
          </p>
          {isAuthenticated && (
            <button
              onClick={onBookAppointment}
              className="px-6 py-3 font-semibold"
              style={{
                backgroundColor: colors.accent,
                color: colors.white,
                borderRadius: radius.lg,
              }}
            >
              קבע תור
            </button>
          )}
        </div>
      )}
    </div>
  );
};
